import config from "appConfig";

const theory = ` <p>
We state here the equations of acoustics for plane waves without dissipation and external sources. These equations are used to determine the wave equation in one dimension. The front wave is plane if all the points belonging to the same plane behave identically. Assuming that the normal to the plane is given by the
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
-axis, all quantities are independent of
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>y</mi>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>z</mi>
</math>
.
<h3 class=sectionHead>Variables characterizing a fluid</h3>
<p>
The state of an infinitesimal volume of fluid can be characterized by a number of interrelated quantities. These quantities are described by functions of space (the
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
variable) and time
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>t</mi>
</math>
. It is assumed that these functions are continuous and differentiable. Four quantities are usually used:
<ul class=itemize1>
<li class=itemize>
   the <span class=cmti-10>pressure </span>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , which is a scalar quantity expressed in Pascal (Pa)
<li class=itemize>
   the <span class=cmti-10>density </span>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ρ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , which is a scalar quantity expressed in kg/m<sup>3</sup>
<li class=itemize>
   the <span class=cmti-10>velocity </span>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
       <mi mathvariant=bold>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , which is a vector quantity expressed in m/s. <em><span class=cmti-10>A priori</span></em>, this vector has three components
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>
   ”. In this Lesson, we will see that in the absence of flow and assuming plane wave, the only non-zero component will be along
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
<li class=itemize>
   the <span class=cmti-10>temperature </span>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , which is a positive scalar that is expressed in Kelvin (K).
</ul>
<p>The sounds are associated with pressure fluctuations. All considered quantities are indeed fluctuating. In acoustics, it is precisely the fluctuating part of these quantities that is of interest. It is therefore usual to split the pressure, the density and the velocity as follows:
<ul class=itemize1>
<li class=itemize>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>t</mi>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo>+</mo>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math> where
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>t</mi>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      is the average pressure that is to say, in general, the atmospheric pressure and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      </math>
      represents the pressure fluctuations. The mean value of
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      </math>
      is zero over time.
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>t</mi>
            <mi>m</mi>
         </mrow>
      </msub>
      </math>
      is assumed to be constant, i.e. invariable in time and space
<li class=itemize>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ρ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the density of the gas at rest and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   represents fluctuations. The mean value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is zero over time
<li class=itemize>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
     <mi mathvariant=bold>v</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=bold>V</mi>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
           <mi mathvariant=bold>v</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi mathvariant=bold>V</mi>
   </math>
   is the speed of a fluid flow and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
         <mi mathvariant=bold>v</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   represents velocity fluctuations.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
     <mi mathvariant=bold>V</mi>
   </math>
   is generally assumed to be zero because it has little effect on sound propagation in most problems (the flow velocity has to exceed several m/s so that the effect is significant)
</ul>
<p>Within the framework of linear acoustics, it is assumed that fluctuations are small quantities compared to their mean values. In the case of velocity, whose mean value is zero in the absence of flow, we will see that the assumption of linear acoustics is equivalent to stating that the particle velocity is small compared to the speed of sound.
<h3 class=sectionHead>The equations of acoustics</h3>
<p>
If we disregard the temperature fluctuations, the equations of acoustics are three in number and couple
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>P</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
</math>
,
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
</math>
. These equations are
<ul class=itemize1>
<li class=itemize>the fundamental relation of dynamics applied to a volume of fluid (named “Euler equation”),
<li class=itemize>the conservation of mass or momentum equation,
<li class=itemize>The equation of state, which is a thermodynamic equation.
</ul>
<h4 class=subsectionHead>Euler equation</h4>
<p>
The Euler equation is the fundamental relation of dynamics applied to a “slice” of fluid of infinitesimal thickness
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
likely to move along
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
(see <a href="#" data-img-src=${config.ASSETS_URL}/01/02/theory/figure-1.svg data-img-alt="Figure 1. A slice of fluid of infinitesimal thickness <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><mi>d</mi></mstyle><mi>x</mi></math> to which the fundamental relation of dynamics is applied.">Figure 1</a>).
<figure class=figure>
<p><img src=${config.ASSETS_URL}/01/02/theory/figure-1.svg alt="Figure 1. A slice of fluid of infinitesimal thickness <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><mi>d</mi></mstyle><mi>x</mi></math> to which the fundamental relation of dynamics is applied."><figcaption class=caption>Figure 1. A slice of fluid of infinitesimal thickness <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><mi>d</mi></mstyle><mi>x</mi></math> to which the fundamental relation of dynamics is applied.</figcaption></figure>
<p>
If we denote by
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi mathvariant=bold>F</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=bold>F</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
</math>
the forces applied by the surrounding fluid to the infinitesimal slice of fluid on the surfaces at
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
, respectively, the fundamental equation of dynamics to the slice of thickness
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
reads
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mi mathvariant=bold>F</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=bold>F</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-bin>+</mo>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>x</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>m</mi>
         <mspace width="10px"/>
         <mi mathvariant=bold>a</mi>
      </math>
   <td class=eq-no>(1)
</table>
<p>
where
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>m</mi>
</math>
is the mass of the portion of fluid and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>a</mi>
</math>
its acceleration. Note that the gravitational force to the slice is assumed negligible compared to other forces. Dividing the previous equation by
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
and since
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <munder class=msub>
            <mrow>
               <mi class=qopname>lim</mi>
               <mo>⁡</mo>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>x</mi>
               <mo class=MathClass-rel>→</mo>
               <mn>0</mn>
            </mrow>
         </munder>
         <mfrac>
            <mrow>
               <mi mathvariant=bold>F</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-close>)</mo>
               <mo class=MathClass-bin>−</mo>
               <mi mathvariant=bold>F</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-bin>+</mo>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>x</mi>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>x</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mi mathvariant=bold>F</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>x</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(2)
</table>
<p>the equation (1) finally reads
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mi mathvariant=bold>F</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>x</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>m</mi>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>x</mi>
            </mrow>
         </mfrac>
         <mi mathvariant=bold>a</mi>
      </math>
   <td class=eq-no>(3)
</table>
<p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
 <mi mathvariant=bold>F</mi>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
 <mi mathvariant=bold>a</mi>
</math>
are vectors carried by the
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
axis. In order to simplify, we can therefore write
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>x</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>m</mi>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>x</mi>
            </mrow>
         </mfrac>
         <mi>a</mi>
      </math>
   <td class=eq-no>(4)
</table>
<p>
where
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>F</mi>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>a</mi>
</math>
are the algebraic values (<em><span class=cmti-10>i.e.</span></em> the norms) of
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
 <mi mathvariant=bold>F</mi>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
 <mi mathvariant=bold>a</mi>
</math>
, respectively. Since
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>F</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>A</mi>
   <mi>P</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
</math>
, with
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>A</mi>
</math>
the area of the slice and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>P</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
</math>
the pressure, and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>m</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>ρ</mi>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>V</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>ρ</mi>
   <mi>A</mi>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
, where
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>V</mi>
</math>
is the volume of a slice, the fundamental equation of dynamics divided by
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>A</mi>
</math>
can be written
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>x</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mi>ρ</mi>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>t</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(5)
</table>
<p>
where the acceleration
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>a</mi>
</math>
has been substituted by
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
      </mrow>
   </mfrac>
</math>
. Equation 5 is the Euler equation. Within the framework of linear acoustics (small perturbations) we can write
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-bin>+</mo>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
</math>
and assume that
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>></mo>
   <mo class=MathClass-rel>></mo>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
</math>
. Assuming further that there is no mean flow and considering that
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>v</mi>
   <mfrac>
      <mrow>
         <mi>∂</mi>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <mi>x</mi>
      </mrow>
   </mfrac>
</math>
is of second order (i.e. negligible), we can write
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>∂</mi>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <mi>t</mi>
      </mrow>
   </mfrac>
</math>
and as
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi>a</mi>
         <mi>t</mi>
         <mi>m</mi>
      </mrow>
   </msub>
</math>
is constant
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mi>∂</mi>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <mi>x</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>∂</mi>
         <mi>p</mi>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <mi>x</mi>
      </mrow>
   </mfrac>
</math>
. Thus the linearized Euler equation eventually writes
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mrow class=boxed>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>t</mi>
               </mrow>
            </mfrac>
         </mrow>
      </math>
   <td class=eq-no>(6)
</table>
<p>This is the first equation of linear acoustics in one dimension.
<h4 class=subsectionHead>The mass conservation equation</h4>
<p>This second equation of acoustics expresses the conservation of mass, i.e. when a quantity of air enters a given volume this necessarily leads to an increase of the mass and therefore, the volume being fixed, to an increase of the density.
<p>
We again consider a slice of thickness
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
at
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
, but the slice does not move. At time
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>t</mi>
</math>
, the mass of the slice is
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>m</mi>
   <mo class=MathClass-open>(</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mi>A</mi>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
and, at time
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>t</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
</math>
, the mass is
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>m</mi>
   <mo class=MathClass-open>(</mo>
   <mi>t</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi>t</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mi>A</mi>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
. The change in mass
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>Δ</mi>
   <mo class=MathClass-open>(</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>m</mi>
   <mo class=MathClass-close>)</mo>
</math>
is then
<table class=equation>
<tr>
   <td style="font-size: 12px">
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mi>Δ</mi>
         <mo class=MathClass-open stretchy=false>(</mo>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>m</mi>
         <mo class=MathClass-close stretchy=false>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mrow>
            <mo fence=true form=prefix>[</mo>
            <mrow>
               <mi>ρ</mi>
               <mo class=MathClass-open>(</mo>
               <mi>t</mi>
               <mo class=MathClass-bin>+</mo>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>t</mi>
               <mo class=MathClass-close>)</mo>
               <mo class=MathClass-bin>−</mo>
               <mi>ρ</mi>
               <mo class=MathClass-open>(</mo>
               <mi>t</mi>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mo fence=true form=postfix>]</mo>
         </mrow>
         <mi>A</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>t</mi>
            </mrow>
         </mfrac>
         <mi>A</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>x</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
      </math>
   <td class=eq-no>(7)
</table>
<p>
The amount of air that enters through the face of abscissa
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
during the time
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
</math>
is
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <msub>
      <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-open>(</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
   <mi>A</mi>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
</math>
. The amount of air exiting through the face of abscissa
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
</math>
is
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <msub>
      <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-open>(</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
   <mi>A</mi>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-bin>+</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
</math>
(see <a href="#" data-img-src=${config.ASSETS_URL}/01/02/theory/figure-2.svg data-img-alt="Figure 2. Illustration of the decrease in density due to a negative difference between the amount of air that enters and exits a fixed infinitesimal slice of air <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><mi>d</mi></mstyle><mi>x</mi></math> during the time <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><mi>d</mi></mstyle><mi>t</mi></math>.">Figure 2</a>). The change in mass of the slice during the time
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
</math>
is then
<table class=equation>
<tr>
   <td style="font-size: 8px">
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mi>Δ</mi>
         <mo class=MathClass-open>(</mo>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>m</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mrow>
            <mo fence=true form=prefix>[</mo>
            <mrow>
               <mi>ρ</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-close>)</mo>
               <mi>A</mi>
               <mi>v</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-close>)</mo>
               <mo class=MathClass-bin>−</mo>
               <mi>ρ</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-bin>+</mo>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>x</mi>
               <mo class=MathClass-close>)</mo>
               <mi>A</mi>
               <mi>v</mi>
               <mo class=MathClass-open>(</mo>
               <mi>x</mi>
               <mo class=MathClass-bin>+</mo>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>x</mi>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mo fence=true form=postfix>]</mo>
         </mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mrow>
                  <mo fence=true form=prefix>(</mo>
                  <mrow>
                     <mi>ρ</mi>
                     <mi>v</mi>
                  </mrow>
                  <mo fence=true form=postfix>)</mo>
               </mrow>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>x</mi>
            </mrow>
         </mfrac>
         <mi>A</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>x</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
      </math>
   <td class=eq-no>(8)
</table>
<figure class=figure>
<p>
   <img src=${config.ASSETS_URL}/01/02/theory/figure-2.svg alt="Figure 2. Illustration of the decrease in density due to a negative difference between the amount of air that enters and exits a fixed infinitesimal slice of air <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><mi>d</mi></mstyle><mi>x</mi></math> during the time <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mstyle mathvariant=normal><mi>d</mi></mstyle><mi>t</mi></math>.">
   <figcaption class=caption>
      Figure 2. Illustration of the decrease in density due to a negative difference between the amount of air that enters and exits a fixed infinitesimal slice of air
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>x</mi>
      </math>
      during the time
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
      </math>.
   </figcaption>
</figure>
<p>Equating the two equations for the mass, one obtains
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>t</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <mrow>
                  <mo fence=true form=prefix>(</mo>
                  <mrow>
                     <mi>ρ</mi>
                     <mi>v</mi>
                  </mrow>
                  <mo fence=true form=postfix>)</mo>
               </mrow>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <mi>x</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(9)
</table>
<p>
As seen in section 1, we have
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-bin>+</mo>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>t</mi>
   <mo class=MathClass-close>)</mo>
</math>
. Since
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
is constant, the partial derivatives are equal:
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mi>∂</mi>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <mi>t</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>∂</mi>
         <msup>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>′</mi>
            </mrow>
         </msup>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <mi>t</mi>
      </mrow>
   </mfrac>
</math>
. Within the framework of linear acoustics (small perturbations), this leads to the linearized momentum conservation equation
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mrow class=boxed>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <mi>x</mi>
               </mrow>
            </mfrac>
         </mrow>
      </math>
   <td class=eq-no>(10)
</table>
<h4 class=subsectionHead>State equation</h4>
<p>This is an equation that establish the relationship between the pressure fluctuations and changes in the volume occupied by a given amount of gas. This equation (also called “constitutive law”) depends on the thermodynamic transformation.
<p>
For an isothermal transformation the equation of ideal gas (it makes a negligible error of assuming the air a perfect gas)
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>P</mi>
   <mi>V</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>n</mi>
   <mi>R</mi>
   <mi>T</mi>
   <mo class=MathClass-rel>=</mo>
   <mstyle class=text>
      <mtext>C</mtext>
   </mstyle>
</math>
, a constant, leads to
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi>P</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-bin>−</mo>
   <mfrac>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>V</mi>
      </mrow>
      <mrow>
         <mi>V</mi>
      </mrow>
   </mfrac>
</math>
or else, as
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>ρ</mi>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mi>V</mi>
      </mrow>
   </mfrac>
</math>
,
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mfrac>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi>P</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>ρ</mi>
      </mrow>
   </mfrac>
</math>
.
<p>
This equation is applicable to an isothermal transformation. But we know (from Lagrange 1759) that the acoustic changes are not isothermal, but adiabatic and reversible, because the heat exchanges between the cold spots and hot spots are too slow (compared to the period of the oscillations). In this case, thermodynamics tells us that the equation
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>P</mi>
   <mi>V</mi>
   <mo class=MathClass-rel>=</mo>
   <mstyle class=text>
      <mtext>C</mtext>
   </mstyle>
</math>
has to be replaced by the equation
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>P</mi>
   <msup>
      <mrow>
         <mi>V</mi>
      </mrow>
      <mrow>
         <mi>γ</mi>
      </mrow>
   </msup>
   <mo class=MathClass-rel>=</mo>
   <mstyle class=text>
      <mtext>C’</mtext>
   </mstyle>
</math>
, where
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>γ</mi>
</math>
is the ratio of specific heats (
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>γ</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>1</mn>
   <mo class=MathClass-punc>.</mo>
   <mn>4</mn>
   <mn>0</mn>
   <mn>2</mn>
</math>
for air) and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle class=text>
      <mtext>C’</mtext>
   </mstyle>
</math>
a constant . The state equation can be written as
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mi>P</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mi>γ</mi>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>ρ</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(11)
</table>
<p>
If small fluctuations are assumed, we can write
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>P</mi>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi>a</mi>
         <mi>t</mi>
         <mi>m</mi>
      </mrow>
   </msub>
</math>and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>ρ</mi>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
and since
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>P</mi>
   <mo class=MathClass-rel>=</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>p</mi>
</math>
and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>ρ</mi>
   <mo class=MathClass-rel>=</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
we finally obtain the linearized state equation
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mrow class=boxed>
            <mfrac>
               <mrow>
                  <mstyle mathvariant=normal>
                     <mi>d</mi>
                  </mstyle>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>t</mi>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>γ</mi>
            <mfrac>
               <mrow>
                  <mstyle mathvariant=normal>
                     <mi>d</mi>
                  </mstyle>
                  <msup>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </mrow>
      </math>
   <td class=eq-no>(12)
</table>
<p>This is the third equation of acoustics.
<h4 class=subsectionHead>Wave equation</h4>
<p>
The equation of establish the link between the density fluctuation
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
and the acoustic pressure
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>p</mi>
</math>. It is then possible to replace the density fluctuation by its expression as a function of the pressure, i.e.
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </mrow>
      <mrow>
         <mi>γ</mi>
         <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>t</mi>
            <mi>m</mi>
         </mrow>
         </msub>
      </mrow>
   </mfrac>
   <mi>p</mi>
</math>. The problem is then reduced to a system of two equations
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mrow>
            <mo fence=true form=prefix>{</mo>
            <mrow>
               <mtable class=aligned>
                  <mtr>
                     <mtd columnalign=right>
                        <mfrac>
                           <mrow>
                              <mi>∂</mi>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <mi>x</mi>
                           </mrow>
                        </mfrac>
                     </mtd>
                     <mtd columnalign=left>
                        <mo class=MathClass-rel>=</mo>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mfrac>
                           <mrow>
                              <mi>∂</mi>
                              <mi>v</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <mi>t</mi>
                           </mrow>
                        </mfrac>
                     </mtd>
                  </mtr>
                  <mtr>
                     <mtd columnalign=right>
                        <mfrac>
                           <mrow>
                              <mi>∂</mi>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <mi>t</mi>
                           </mrow>
                        </mfrac>
                     </mtd>
                     <mtd columnalign=left>
                        <mo class=MathClass-rel>=</mo>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <msup>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <mfrac>
                           <mrow>
                              <mi>∂</mi>
                              <mi>v</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <mi>x</mi>
                           </mrow>
                        </mfrac>
                     </mtd>
                  </mtr>
               </mtable>
            </mrow>
         </mrow>
      </math>
   <td class=eq-no>(13)
</table>
<p>
where
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msup>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>γ</mi>
         <msub>
            <mrow>
               <mi>P</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>t</mi>
               <mi>m</mi>
            </mrow>
         </msub>
      </mrow>
      <mrow>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </mrow>
   </mfrac>
</math>. It will be demonstrated hereafter that
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>c</mi>
</math>
is the speed of sound. Deriving the first equation with respect to
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
and the second with respect to
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>t</mi>
</math>
leads to
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mrow>
            <mo fence=true form=prefix>{</mo>
            <mrow>
               <mtable class=aligned>
                  <mtr>
                     <mtd columnalign=right>
                        <mfrac>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mi>∂</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <msup>
                                 <mrow>
                                    <mi>x</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                           </mrow>
                        </mfrac>
                     </mtd>
                     <mtd columnalign=left>
                        <mo class=MathClass-rel>=</mo>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mfrac>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mi>∂</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mi>v</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <mi>t</mi>
                              <mi>∂</mi>
                              <mi>x</mi>
                           </mrow>
                        </mfrac>
                     </mtd>
                  </mtr>
                  <mtr>
                     <mtd columnalign=right>
                        <mfrac>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mi>∂</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mi>p</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <msup>
                                 <mrow>
                                    <mi>t</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                           </mrow>
                        </mfrac>
                     </mtd>
                     <mtd columnalign=left>
                        <mo class=MathClass-rel>=</mo>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi>ρ</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <msup>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <mfrac>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mi>∂</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                              <mi>v</mi>
                           </mrow>
                           <mrow>
                              <mi>∂</mi>
                              <mi>x</mi>
                              <mi>∂</mi>
                              <mi>t</mi>
                           </mrow>
                        </mfrac>
                     </mtd>
                  </mtr>
               </mtable>
            </mrow>
         </mrow>
      </math>
   <td class=eq-no>(14)
</table>
<p>which then gives
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mfrac>
            <mrow>
               <msup>
                  <mrow>
                     <mi>∂</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <msup>
                  <mrow>
                     <mi>x</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <msup>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
         <mfrac>
            <mrow>
               <msup>
                  <mrow>
                     <mi>∂</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <msup>
                  <mrow>
                     <mi>t</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(15)
</table>
<p>or else
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mrow class=boxed>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </mrow>
      </math>
   <td class=eq-no>(16)
</table>
<p>This the 1D wave equation for pressure.
<p>
Deriving the first equation with respect to
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>t</mi>
</math>
and the second with respect to
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>x</mi>
</math>
leads to the same equation for the particle velocity
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <mrow class=boxed>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </mrow>
      </math>
   <td class=eq-no>(17)
</table>
<p>
This is the 1D wave equation for particle velocity. Note that the density fluctuations
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
obey also to the wave equation.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
 <dt>[1]
 <dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
 <dt>[2]
 <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
 <dt>[3]
 <dd>Samuel Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
